import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  CreditCard,
  Download,
  LifeBuoy,
  MessageCircle,
  Search,
  ShieldCheck,
  Smartphone,
  Wallet,
  X,
} from "lucide-react";

const TOPICS = [
  { icon: Wallet, title: "Wallet & float", copy: "Top-ups, reserved funds, payouts and limits.", articles: 12, tone: "blue" },
  { icon: Smartphone, title: "Selling airtime", copy: "Networks, receipts and what pending really means.", articles: 9, tone: "violet" },
  { icon: CreditCard, title: "Commission & pricing", copy: "How your 3.2% is earned and when it settles.", articles: 7, tone: "mint" },
  { icon: Download, title: "Statements & reports", copy: "Monthly statements, CSV exports and TRA records.", articles: 6, tone: "amber" },
  { icon: ShieldCheck, title: "Security", copy: "PINs, 2FA, device sessions and fraud safety.", articles: 8, tone: "rose" },
  { icon: BookOpen, title: "Odoo & accounting", copy: "How evocha posts to Odoo and reconciles.", articles: 5, tone: "cyan" },
];

const FAQS = [
  {
    q: "A sale is pending. Should I repeat it?",
    a: "No. Pending means we sent the request but the network has not confirmed yet. Funds stay reserved and we keep checking. Repeating it can send airtime twice and you would carry the loss. Open the transaction to see the live status; we update it automatically.",
    tag: "Selling airtime",
  },
  {
    q: "When does a top-up reach my wallet?",
    a: "M-Pesa, Mixx by Yas and Airtel Money top-ups usually arrive within seconds of approving on your phone. Bank transfers are credited the same business day if sent before 15:00 EAT. If a mobile money top-up has not arrived after 5 minutes, raise a support request with the provider reference.",
    tag: "Wallet",
  },
  {
    q: "How is my commission calculated?",
    a: "Commission is a percentage of the airtime value sold, set by your tier. Standard agents earn 3.2%. It is credited to your commission bucket as soon as a sale succeeds and can be moved to your available balance. Commission on failed sales is reversed with the sale.",
    tag: "Commission",
  },
  {
    q: "Why can I not sell more than TZS 2,000,000 in one sale?",
    a: "Single-sale and daily limits protect both you and your customers. Your current single-sale limit is TZS 20,000 and your daily volume limit is TZS 50,000,000. You can request a higher limit from the wallet page; increases are reviewed by the risk team.",
    tag: "Account",
  },
  {
    q: "What happens if a sale fails?",
    a: "If the provider confirms the sale did not complete, your reserved funds are returned automatically and the receipt shows Not completed. If the outcome is unknown, the sale moves to Under review — we resolve it with the provider and never mark an unknown sale as failed.",
    tag: "Selling airtime",
  },
  {
    q: "Can I use the same account on web, app and USSD?",
    a: "Yes. One wallet, one PIN and one transaction history across the agent portal, the evocha mobile app and USSD. A sale started on one channel appears everywhere immediately.",
    tag: "Account",
  },
  {
    q: "How do I change my transaction PIN?",
    a: "Go to Profile → Security → Change PIN. You will need your current PIN and an SMS code. If you have forgotten your PIN, contact support — we will verify your identity and reset it, never ask you to send the PIN itself.",
    tag: "Security",
  },
  {
    q: "Where can I download my monthly statement?",
    a: "Statements → choose a month → Download PDF or Export CSV. Statements close at 23:59 EAT on the last day of the month and remain available for 24 months.",
    tag: "Statements",
  },
];

const GUIDES = [
  { title: "Your first sale, step by step", time: "4 min read", tag: "Getting started" },
  { title: "Keeping enough float for the evening rush", time: "3 min read", tag: "Wallet" },
  { title: "Reading a receipt and provider reference", time: "2 min read", tag: "Selling airtime" },
  { title: "Reconciling evocha with Odoo", time: "6 min read", tag: "Accounting" },
];

export function Help() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<number | null>(0);
  const [tag, setTag] = useState("All");

  const tags = useMemo(() => ["All", ...Array.from(new Set(FAQS.map((f) => f.tag)))], []);

  const faqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      const matchesTag = tag === "All" || f.tag === tag;
      const matchesQuery = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return matchesTag && matchesQuery;
    });
  }, [query, tag]);

  return (
    <div className="stack-6">
      <section className="help-hero">
        <div className="help-hero-inner">
          <span className="ev-eyebrow" style={{ color: "rgba(255,255,255,0.7)" }}>Help centre</span>
          <h2>What can we help you with?</h2>
          <p>Short answers for the things agents ask most. Still stuck? Support is one tap away.</p>
          <div className="help-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help — e.g. pending sale, top-up, commission"
              aria-label="Search help articles"
            />
            {query && <button onClick={() => setQuery("")} aria-label="Clear search"><X size={16} /></button>}
          </div>
        </div>
        <div className="help-orb help-orb-a" />
        <div className="help-orb help-orb-b" />
      </section>

      {query ? (
        <section className="ev-card">
          <div className="ev-card-head">
            <div>
              <h3 className="ev-card-title">Search results</h3>
              <p className="ev-caption">{faqs.length} answer{faqs.length === 1 ? "" : "s"} for “{query}”</p>
            </div>
          </div>
          <FaqList faqs={faqs} open={open} setOpen={setOpen} />
        </section>
      ) : (
        <>
          <div className="topic-grid">
            {TOPICS.map(({ icon: Icon, title, copy, articles, tone }) => (
              <button key={title} className="topic-card" onClick={() => setQuery(title.split(" ")[0] ?? "")}>
                <span className={`topic-icon topic-${tone}`}><Icon size={19} /></span>
                <strong>{title}</strong>
                <span className="topic-copy">{copy}</span>
                <span className="topic-foot">{articles} articles <ArrowRight size={14} /></span>
              </button>
            ))}
          </div>

          <div className="help-grid">
            <section className="ev-card">
              <div className="ev-card-head">
                <div>
                  <h3 className="ev-card-title">Frequently asked</h3>
                  <p className="ev-caption">The answers that save agents the most time.</p>
                </div>
                <span className="ev-badge ev-badge-neutral">{FAQS.length}</span>
              </div>
              <div className="faq-tags">
                {tags.map((t) => (
                  <button key={t} className="ev-chip" aria-pressed={tag === t} onClick={() => setTag(t)}>{t}</button>
                ))}
              </div>
              <FaqList faqs={faqs} open={open} setOpen={setOpen} />
            </section>

            <aside className="stack-5">
              <section className="ev-card ev-card-pad">
                <span className="ev-eyebrow">Guides</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Learn the flow</h3>
                <div className="guide-list">
                  {GUIDES.map((g) => (
                    <a key={g.title} href="#guide" className="guide-row">
                      <span className="guide-icon"><BookOpen size={16} /></span>
                      <span><strong>{g.title}</strong><em>{g.tag} · {g.time}</em></span>
                      <ArrowRight size={14} />
                    </a>
                  ))}
                </div>
              </section>

              <section className="ev-card ev-card-pad help-contact">
                <span className="help-contact-icon"><LifeBuoy size={20} /></span>
                <h3 className="ev-h3">Still stuck?</h3>
                <p>A real person can look at your account and the provider response.</p>
                <Link to="/support" className="ev-btn ev-btn-gradient ev-btn-block"><MessageCircle size={16} /> Contact support</Link>
                <span className="ev-caption ev-center">07:00 – 22:00 EAT · median reply 4 min</span>
              </section>
            </aside>
          </div>

          <section className="ev-card ev-card-pad help-status">
            <div className="ev-row ev-gap-3">
              <span className="live-dot" />
              <div><strong>All systems operational</strong><span className="ev-caption">Lipawakala · Vodacom · Airtel · Tigo · Halotel · SMS</span></div>
            </div>
            <span className="ev-caption">Last checked 2 minutes ago</span>
            <a href="#status" className="ev-btn ev-btn-sm ev-btn-ghost">Status page <ArrowRight size={14} /></a>
          </section>
        </>
      )}
    </div>
  );
}

function FaqList({ faqs, open, setOpen }: { faqs: typeof FAQS; open: number | null; setOpen: (n: number | null) => void }) {
  if (faqs.length === 0) {
    return (
      <div className="ev-empty">
        <Search />
        <strong>No answers matched</strong>
        <p className="ev-caption">Try fewer words, or send the question to support.</p>
      </div>
    );
  }
  return (
    <div className="faq-list">
      {faqs.map((f, i) => (
        <div className={open === i ? "faq faq-open" : "faq"} key={f.q}>
          <button className="faq-q" onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}>
            <span>{f.q}</span>
            <ChevronDown size={17} />
          </button>
          {open === i && <div className="faq-a"><p>{f.a}</p><span className="ev-badge ev-badge-neutral">{f.tag}</span></div>}
        </div>
      ))}
    </div>
  );
}
